import React from 'react';
import Checkbox from '@gik/ui/Checkbox';
import { Button, Input } from '@gik/ui';
import { Field } from 'react-final-form';
import { FieldState, FormApi } from 'final-form';
import Form, { FormGroup, ValidationError, ValidationErrors } from '@gik/ui/Form';
import * as validator from '@gik/core/utils/validator';
import { PropsWithClassName } from '@gik/core/utils/ReactUtils';
import i18n from '@gik/i18n';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { Organizer, OrganizerInvitation } from '@gik/core/models/gik/InkindPage';
import bemBlock from '@gik/core/utils/bemBlock';

export const inviteSectionBlockName = 'invite-section';

interface InviteSectionErrors extends ValidationErrors {
  emailAddress?: ValidationError;
  canEdit?: ValidationError;
  notifications?: ValidationError;
}

export interface IInviteSectionProps {
  pageOwner: Organizer;
  pageOrganizers: Organizer[];
  pageInvitees: Organizer[];
  routeId: string;
  onSendInvitation: (invitation: OrganizerInvitation) => void | Promise<void>;
}

export default function InviteSection({
  // for custom validation
  pageOwner,
  pageOrganizers,
  pageInvitees,
  onSendInvitation,
  className,
}: React.PropsWithChildren<PropsWithClassName<IInviteSectionProps>>): JSX.Element {
  const bem = bemBlock(inviteSectionBlockName);

  const customValidateForm = (values: OrganizerInvitation): InviteSectionErrors => {
    const errors: InviteSectionErrors = {};

    if (validator.isEmpty(values.emailAddress)) {
      errors.emailAddress = { message: i18n.t('validation.requiredEmail') };
    }

    if (validator.isString(values.emailAddress) && !validator.isEmail(values.emailAddress as string)) {
      errors.emailAddress = { message: i18n.t('validation.email') };
    }

    const isAlreadyOwner = pageOwner && pageOwner.email.localeCompare(values.emailAddress) === 0;

    const isAlreadyOrganizer =
      pageOrganizers && pageOrganizers.some(organizer => organizer.email.localeCompare(values.emailAddress) === 0);

    if (isAlreadyOwner || isAlreadyOrganizer) {
      errors.emailAddress = { message: i18n.t(translationKeys.emailAlreadyOrganizerValidation) };
    }

    const isAlreadyInvited =
      pageInvitees && pageInvitees.some(organizer => organizer.email.localeCompare(values.emailAddress) === 0);

    if (isAlreadyInvited) {
      errors.emailAddress = { message: i18n.t(translationKeys.emailAlreadyInvitedValidation) };
    }

    return errors;
  };

  const handleSubmit = async (values: OrganizerInvitation, form: FormApi) => {
    await onSendInvitation(values);
    setTimeout(() => form.restart());
  };

  // manual Form because of custom validation for email
  return (
    <div className={bem(null, null, className)}>
      <Form
        onSubmit={handleSubmit}
        validate={customValidateForm}
        initialValues={{ canEdit: true, notifications: true }}
        vertical
        render={props => {
          const { valid, submitting } = props;

          return (
            <>
              <div className={bem('input')}>
                <Field name="emailAddress" type="email">
                  {({ input, meta: fieldState }) => {
                    return (
                      <FormGroup fieldState={fieldState as FieldState<string>} vertical>
                        <Input {...input} placeholder={i18n.t(translationKeys.emailAddress)} />
                      </FormGroup>
                    );
                  }}
                </Field>
              </div>

              <div className={bem('checkbox')}>
                <Field name="canEdit" type="checkbox">
                  {({ input, meta: fieldState }) => {
                    return (
                      <FormGroup fieldState={fieldState as FieldState<boolean>}>
                        <Checkbox
                          {...input}
                          checked={input.value || input.checked}
                          variant="primary"
                          label={i18n.t(translationKeys.canEditPage)}
                        />
                      </FormGroup>
                    );
                  }}
                </Field>

                <Field name="notifications" type="checkbox">
                  {({ input, meta: fieldState }) => {
                    return (
                      <FormGroup fieldState={fieldState as FieldState<boolean>}>
                        <Checkbox
                          {...input}
                          checked={input.value || input.checked}
                          variant="primary"
                          label={i18n.t(translationKeys.emailAllNotifications)}
                        />
                      </FormGroup>
                    );
                  }}
                </Field>
              </div>

              <div className={bem('button')}>
                <Button variant="primary" type="submit" loading={submitting} disabled={!valid}>
                  {i18n.t(translationKeys.inviteOrganizersButton)}
                </Button>
              </div>
            </>
          );
        }}
      />
    </div>
  );
}
